import { SvgIcon } from '@modules/icons/code/Icon'
import { Card } from '@modules/shadcn/ui/Card/Card'

import { SimulationImageUploader } from '@ai-color-modules/simulation/SimulationImageUploader/SimulationImageUploader'

interface SimulationUploadCardProps {
  s3Upload: (file: File) => Promise<void>
  uploading: boolean
  title: string
  inputId: string
  ariaLabel: string
  extraDialogContent?: React.ReactNode
  /** ダイアログ内、プレビューより上に差し込む注意書き。背の高い PDF プレビューでも fold 上に残す。 */
  dialogNoticeContent?: React.ReactNode
  /** confirmDisabled のときに確定ボタンの近くへ出す理由。 */
  confirmDisabledReason?: string
  /** ドロップゾーンの直下に出す受付条件（対応形式・容量上限・対象の図面種別など）。 */
  requirementsContent?: React.ReactNode
  /** DialogFooter 左端に差し込む要素。スクロールで流れると困る操作（「しばらく表示しない」等）用。 */
  dialogFooterContent?: React.ReactNode
  /** input の accept 属性。既定 'image/*' (AI カラー既定)。積算トップ等 PDF を扱うホストは上書きする。 */
  accept?: string
  /** ダイアログ内プレビューの種類。既定 'image'。PDF を扱うホストは 'pdf' に切り替える。 */
  previewMode?: 'image' | 'pdf'
  /** 画像以外を扱うホスト向けに HEIC変換・圧縮をスキップする。既定 false (AI カラーの挙動を変えない) */
  skipCompression?: boolean
  dialogTitle?: string
  dialogDescription?: string
  dialogConfirmLabel?: string
  dialogCancelLabel?: string
  /** 右側 Tip ボックスの文言。既定は AI カラーの案内文。 */
  tipText?: string
  /** ダイアログの確定ボタンをホスト側の条件で押させない (必須の追加入力がある場合など)。 */
  confirmDisabled?: boolean
  /** アップロード失敗時にエラー文の下へ添える一文（`SimulationImageUploader` の同名 prop）。 */
  uploadErrorNote?: string
}

export const SimulationUploadCard: React.FC<SimulationUploadCardProps> = ({
  s3Upload,
  uploading,
  title,
  inputId,
  ariaLabel,
  extraDialogContent,
  dialogNoticeContent,
  confirmDisabledReason,
  requirementsContent,
  dialogFooterContent,
  accept,
  previewMode,
  skipCompression,
  dialogTitle,
  dialogDescription,
  dialogConfirmLabel,
  dialogCancelLabel,
  tipText = 'AIを効果的に使うために、建物全体が入るような画像をアップロードしてください。',
  confirmDisabled,
  uploadErrorNote,
}) => {
  return (
    <Card className="w-full border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-6 p-6">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-1 flex-col gap-3">
            <SimulationImageUploader
              accept={accept}
              ariaLabel={ariaLabel}
              confirmDisabled={confirmDisabled}
              confirmDisabledReason={confirmDisabledReason}
              dialogCancelLabel={dialogCancelLabel}
              dialogConfirmLabel={dialogConfirmLabel}
              dialogDescription={dialogDescription}
              dialogFooterContent={dialogFooterContent}
              dialogNoticeContent={dialogNoticeContent}
              dialogTitle={dialogTitle}
              extraDialogContent={extraDialogContent}
              inputId={inputId}
              previewMode={previewMode}
              s3Upload={s3Upload}
              skipCompression={skipCompression}
              uploadErrorNote={uploadErrorNote}
              uploading={uploading}
            />
            {/* 受付条件はファイル選択欄の直下（PdM FB 2026-10-08 #6）。
                右の Tip ボックスへ入れると「コツ」に見えて条件として読まれない */}
            {requirementsContent}
          </div>
          <div className="max-w-[180px] shrink-0 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800 md:w-64">
            <p className="mb-2 h-6">
              <SvgIcon className="max-h-full" />
            </p>
            <p className="text-sm">{tipText}</p>
          </div>
        </div>
      </div>
    </Card>
  )
}
